// IM-UX-003 — Descoberta proativa, recomendação e ativação de fontes. O InteliMove procura fontes públicas a partir do
// contexto da organização (empresa, concorrentes, empresas-alvo, ICPs, segmentos, CNAE, análises), verifica cada uma e
// explica por que recomenda. A pessoa ativa (uma ou várias), descarta, restaura ou ajusta a periodicidade. Nenhuma fonte
// é ativada sem decisão humana; o cadastro manual continua como alternativa, com as mesmas regras de deduplicação.
import { z } from 'zod';

export const SOURCE_RECOMMENDATION_CATEGORIES = ['EMPRESA', 'CONCORRENTE', 'MERCADO', 'OFICIAL', 'ICP_SEGMENTO'] as const;
export type SourceRecommendationCategory = (typeof SOURCE_RECOMMENDATION_CATEGORIES)[number];
export const SOURCE_CATEGORY_LABEL: Record<SourceRecommendationCategory, string> = {
  EMPRESA: 'Sua empresa', CONCORRENTE: 'Concorrentes', MERCADO: 'Mercado e setor', OFICIAL: 'Oficiais e regulatórias', ICP_SEGMENTO: 'ICP e empresas-alvo',
};

export const SOURCE_CLASSES = ['OFICIAL', 'INSTITUCIONAL', 'GOVERNAMENTAL', 'REGULATORIA', 'SETORIAL', 'ESPECIALIZADA', 'CONCORRENTE', 'SOCIAL', 'A_VALIDAR'] as const;
export type SourceClass = (typeof SOURCE_CLASSES)[number];
export const SOURCE_CLASS_LABEL: Record<SourceClass, string> = {
  OFICIAL: 'oficial da empresa', INSTITUCIONAL: 'institucional', GOVERNAMENTAL: 'governamental', REGULATORIA: 'regulatória', SETORIAL: 'setorial',
  ESPECIALIZADA: 'especializada', CONCORRENTE: 'de concorrente', SOCIAL: 'rede social', A_VALIDAR: 'indicação a validar',
};

export type SourceAvailability = 'DISPONIVEL' | 'INDISPONIVEL' | 'NAO_VERIFICADA';
export const SOURCE_AVAILABILITY_LABEL: Record<SourceAvailability, string> = { DISPONIVEL: 'disponível', INDISPONIVEL: 'indisponível', NAO_VERIFICADA: 'não verificada' };

export type SourceRecommendationKind = 'rss' | 'atom' | 'page' | 'youtube' | 'social';
export type SourceRecommendationStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';
export type SourceSchedule5 = 'MANUAL' | 'DIARIA' | 'SEMANAL' | 'QUINZENAL' | 'MENSAL';

export interface SourceRecommendationDto {
  id: string;
  name: string;
  url: string;
  /** Feed que será lido (página com feed declarado, canal do YouTube). */
  feedUrl: string | null;
  kind: SourceRecommendationKind;
  network: string | null;
  pageType: string | null;
  category: SourceRecommendationCategory | null;
  sourceClass: SourceClass;
  /** Empresa, concorrente, empresa-alvo ou assunto a que a fonte se refere. */
  related: { type: string; name: string } | null;
  reason: string;
  /** Confiabilidade da fonte (não do conteúdo): alta para oficial/governamental verificada, baixa para indicação a validar. */
  reliability: 'ALTA' | 'MEDIA' | 'BAIXA';
  availability: SourceAvailability;
  checkDetail: string | null;
  lastCheckedAt: string | null;
  suggestedSchedule: SourceSchedule5;
  status: SourceRecommendationStatus;
  /** Recomendação feita pelo InteliMove a partir do contexto × descoberta no site informado (fluxo antigo). */
  origin: 'RECOMENDACAO' | 'DESCOBERTA_NO_SITE';
  method: string;
  originalUrl: string | null;
  sourceId: string | null;
  createdAt: string;
  decidedAt: string | null;
}

/** Contagens únicas da página de Fontes e do painel (mesma consulta, mesma organização, mesmos estados). */
export interface SourcesOverviewDto {
  /** Recomendações aguardando revisão (pendentes, disponíveis ou ainda não verificadas, ainda não monitoradas). */
  recommended: number;
  /** Fontes monitoradas em operação (ativas, sem falha). */
  active: number;
  paused: number;
  /** IM-XPR-005: perfis e canais identificados que dependem de integração (não contam como ativos nem como falha). */
  awaitingIntegration?: number;
  /** Fontes com falha na coleta ou que exigem ação + recomendações indisponíveis na última verificação. */
  problems: number;
  failing: number;
  unavailableRecommendations: number;
  dismissed: number;
  /** Recomendações já ativadas (histórico). */
  activated: number;
  latestRun: SourceDiscoveryRunDto | null;
  canDiscover: { companySite: string | null; competitorsWithSite: number; targetsWithSite: number };
}

export interface SourceDiscoveryRunDto {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'PARTIAL' | 'FAILED';
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  stats: { candidates?: number; checked?: number; recommended?: number; created?: number; updated?: number; alreadyActive?: number; invalid?: number; duplicates?: number; revalidated?: number };
  notes: string[];
  error: string | null;
}

export const SOURCE_VIEWS = ['recomendadas', 'ativas', 'problemas', 'descartadas', 'pausadas'] as const;
export type SourceView = (typeof SOURCE_VIEWS)[number];

const schedule = z.enum(['MANUAL', 'DIARIA', 'SEMANAL', 'QUINZENAL', 'MENSAL']);
export const DiscoverRecommendationsRequest = z.object({ force: z.boolean().default(false) }).strict();
export type DiscoverRecommendationsRequest = z.infer<typeof DiscoverRecommendationsRequest>;
export const ActivateRecommendationsRequest = z.object({
  items: z.array(z.object({ id: z.string().uuid(), schedule: schedule.optional() }).strict()).min(1).max(25),
}).strict().refine((v) => new Set(v.items.map((i) => i.id)).size === v.items.length, 'Itens repetidos.');
export type ActivateRecommendationsRequest = z.infer<typeof ActivateRecommendationsRequest>;
export const UpdateRecommendationRequest = z.object({ schedule }).strict();

export interface ActivationResultDto {
  id: string;
  outcome: 'ACTIVATED' | 'ALREADY_ACTIVE' | 'ALREADY_DECIDED' | 'FAILED' | 'NOT_FOUND';
  sourceId: string | null;
  sourceName: string | null;
  message: string | null;
}
